/**
 * card-flip plugin
 *
 * Opt-in via `data-card-flip="true"` on the section root. Clicking a card:
 *
 *   1. Navigates the carousel to that card's index — via the shared `seekTo`
 *      helper, so it works in both the "all slides fit" desktop case (class
 *      rotation + follower sync) and the translating mobile case.
 *
 *   2. Toggles a persistent flip on the card, showing the OPPOSITE of whatever
 *      image is currently visible. Clicking again flips it back.
 *
 * The flip is layered ON TOP of the section's existing CSS auto-flip (the
 * active slide and any hovered card auto-show the back image). To make the
 * click a true toggle even on a card the auto-flip is already showing, the
 * explicit `is-flip-front` / `is-flip-back` classes carry higher CSS
 * specificity than the auto-flip rules (see section-card-carousel.scss), and
 * the desired direction is read from the live computed opacity of the back
 * image — so the first click always visibly flips regardless of auto state.
 *
 * Stopping autoplay on tap is owned by the `autoplay` plugin: Swiper's
 * `touchEnd` (already one of autoplay's interaction signals) fires on every
 * tap/click too, so a card tap stops autoplay without anything extra here.
 * This plugin does navigation + flip only.
 *
 * Why Swiper's `tap` event and not a native DOM `click`: on touch devices the
 * browser suppresses the synthesized `click` once Swiper handles the touchmove,
 * so per-slide `click` listeners fire on desktop (mouse) but not on
 * mobile/tablet. Swiper's `tap` fires for both, and only for a genuine tap —
 * `allowClick` flips to `false` the moment a finger moves past the drag
 * threshold, so swipes never flip a card.
 */

import { seekTo } from '../utils/seek.js';

const FLIP_BACK = 'is-flip-back';
const FLIP_FRONT = 'is-flip-front';
const BACK_OPACITY_THRESHOLD = 0.5;

// Touch devices emit a "ghost" duplicate of a tap ~300ms later. With Swiper's
// `simulateTouch` on (required so desktop mouse clicks also emit `tap`), the
// browser's compatibility mouse pointer event after a real touch re-enters
// Swiper's touch handling and fires a SECOND `tap`. Swallow a repeat tap on
// the same slide inside this window so one physical tap = one flip; deliberate
// re-taps to toggle a card back are always far slower than this.
const GHOST_TAP_MS = 400;

const handlersBySwiper = new WeakMap();

/**
 * Is the back (hover) image currently the visible one? Reads computed opacity
 * so it reflects every source of flip — base, :hover, active-slide auto-flip,
 * and any explicit is-flip-* class already applied. A mid-transition value
 * resolves via a 0.5 threshold.
 */
function showsBackImage(content) {
  const hoverImg = content.querySelector('.card-carousel__image--hover');
  if (!hoverImg) return false;
  const opacity = parseFloat(getComputedStyle(hoverImg).opacity);
  return !Number.isNaN(opacity) && opacity > BACK_OPACITY_THRESHOLD;
}

function toggleFlip(content) {
  // Once a card has an explicit flip class, toggle purely on its presence —
  // deterministic, and independent of any mid-transition opacity value or of
  // the auto-flip state (which the explicit classes out-specify in CSS). Only
  // the FIRST tap (no explicit class yet) reads the currently-visible image,
  // so the card flips to the opposite of whatever the auto/hover state shows.
  if (content.classList.contains(FLIP_BACK)) {
    content.classList.remove(FLIP_BACK);
    content.classList.add(FLIP_FRONT);
  } else if (content.classList.contains(FLIP_FRONT)) {
    content.classList.remove(FLIP_FRONT);
    content.classList.add(FLIP_BACK);
  } else if (showsBackImage(content)) {
    content.classList.add(FLIP_FRONT);
  } else {
    content.classList.add(FLIP_BACK);
  }
}

export const cardFlip = {
  name: 'card-flip',

  matches(_el, section) {
    return section?.dataset?.cardFlip === 'true';
  },

  afterInit(swiper, ctx) {
    const section = ctx.parentSwiperSection;
    const syncId = section?.dataset?.syncId || null;
    const { registry } = ctx;

    // Swiper sets `clickedSlide` / `clickedIndex` immediately before emitting
    // `tap`, so they're populated by the time this runs. A tap on a gap
    // between slides leaves `clickedSlide` undefined — bail in that case.
    // `lastSlide` / `lastTapAt` filter the touch ghost tap (see GHOST_TAP_MS):
    // only a same-slide repeat inside the window is dropped, so fast taps
    // across different cards still register.
    let lastSlide = null;
    let lastTapAt = 0;

    const onTap = () => {
      if (swiper.destroyed) return;
      const slide = swiper.clickedSlide;
      const index = swiper.clickedIndex;
      if (!slide || index == null || index < 0) return;

      const now = Date.now();
      if (slide === lastSlide && now - lastTapAt < GHOST_TAP_MS) return;
      lastSlide = slide;
      lastTapAt = now;

      seekTo(swiper, registry, syncId, index);
      const content = slide.querySelector('.card-carousel__card-content') || slide;
      toggleFlip(content);
    };
    swiper.on('tap', onTap);
    handlersBySwiper.set(swiper, onTap);
  },

  onDestroy(swiper) {
    const onTap = handlersBySwiper.get(swiper);
    if (onTap) {
      try {
        swiper.off('tap', onTap);
      } catch {
        // swiper already destroyed; its listeners are gone.
      }
    }
    handlersBySwiper.delete(swiper);
  },
};

export default cardFlip;
